import { Button, Progress } from 'antd'
import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import Numeral from 'numeral'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useAccount } from 'wagmi'

import workingStats from '@/assets/working.png'
import DataLoading from '@/components/DataLoading'
import EmptyData from '@/components/EmptyData'
import { CardContent } from '@/components/PosCard'
import {
  Icon,
  IconContainer,
  PosName,
} from '@/components/PosCard/PosCard.module'
import { useLocalStorage } from '@/context/localstorage-context'
import type { PosValidatorDetail } from '@/types/pos-staker'
import { cutString, formatBalance } from '@/utils'
import { useQueryPosStakeInfo } from '@/utils/queries'
import { useIwan } from '@/utils/useWebSocket'

import PosStatisticsModule from '../pos-statistics'
import {
  DetailData,
  DetailTitle,
  InfoData,
  InfoItem,
  StakePosContainer,
} from '../Pos.module'

import ExitModal from './exit-modal'
import ModifyModal from './modify-modal'
import TopupModal from './topup-modal'

export default function StakePos() {
  const { theme } = useLocalStorage()
  const { address } = useAccount()
  const [showExitModal, setShowExitModal] = useState(false)
  const [showTopUpModal, setShowTopUpModal] = useState(false)
  const [showModifyModal, setShowModifyModal] = useState(false)
  const isDark = useMemo(() => theme === 'dark', [theme])
  const { data: posStakeInfo, isLoading: isQueryPosStakeInfoLoading } =
    useQueryPosStakeInfo()

  const { data: validatorStakeInfo } = useIwan<PosValidatorDetail[]>(
    ['getValidatorStakeInfo'],
    {
      address,
    },
    !!address,
  )

  const { data: validatorTotalIncentive } = useIwan(
    ['getValidatorTotalIncentive'],
    {
      address:
        validatorStakeInfo && validatorStakeInfo[0]
          ? validatorStakeInfo[0].address
          : '',
    },
    !!(validatorStakeInfo && validatorStakeInfo.length > 0),
  )

  const myRewards = useMemo(() => {
    if (validatorTotalIncentive && validatorTotalIncentive.length !== 0) {
      const ret = formatBalance(validatorTotalIncentive[0].amount)
      return Numeral(ret).format('0,0')
    } else {
      return '0'
    }
  }, [validatorTotalIncentive])

  const stakerInfo = useMemo(() => {
    if (
      validatorStakeInfo &&
      validatorStakeInfo.length !== 0 &&
      posStakeInfo &&
      posStakeInfo.stakers &&
      posStakeInfo.stakers.length > 0
    ) {
      return posStakeInfo.stakers.find(
        (v: any) =>
          v.address.toLowerCase() ===
          validatorStakeInfo[0].address.toLowerCase(),
      )
    } else {
      return null
    }
  }, [validatorStakeInfo, posStakeInfo])

  const delegationStake = useMemo(() => {
    if (validatorStakeInfo && validatorStakeInfo.length !== 0) {
      const ret = validatorStakeInfo[0].clients
        .reduce(
          (prev, curr) => prev.plus(formatBalance(curr.amount)),
          new BigNumber(0),
        )
        .toString(10)
      return Numeral(ret).format('0,0')
    } else {
      return '0'
    }
  }, [validatorStakeInfo])

  const myStakeAmount = useMemo(() => {
    if (validatorStakeInfo && validatorStakeInfo.length !== 0) {
      const ret = new BigNumber(formatBalance(validatorStakeInfo[0].amount))
        .plus(
          validatorStakeInfo[0].partners.reduce(
            (prev, curr) => prev.plus(formatBalance(curr.amount)),
            new BigNumber(0),
          ),
        )
        .toString(10)
      return Numeral(ret).format('0,0')
    } else {
      return '0'
    }
  }, [validatorStakeInfo])

  const pendingWithdrawal = useMemo(() => {
    if (validatorStakeInfo && validatorStakeInfo.length !== 0) {
      if (validatorStakeInfo[0].nextLockEpochs === 0) {
        return myStakeAmount
      } else {
        return '0'
      }
    } else {
      return '0'
    }
  }, [validatorStakeInfo])

  const isEmpty = useMemo(
    () =>
      !address ||
      (validatorStakeInfo !== null && validatorStakeInfo.length === 0) ||
      stakerInfo === null,
    [validatorStakeInfo, stakerInfo],
  )

  const isLoading = useMemo(
    () =>
      (!!address && validatorStakeInfo === null) || isQueryPosStakeInfoLoading,
    [validatorStakeInfo, isQueryPosStakeInfoLoading],
  )

  return (
    <StakePosContainer>
      <PosStatisticsModule />
      <div className="bg-white rounded-2xl flex flex-col gap-2 items-start p-[20px] dark:bg-dark-222 min-h-[100px]">
        <div className="text-[20px] font-normal text-light-0 mb-4 flex gap-4 dark:text-white">
          <div>My PoS Validator Node</div>
        </div>
        <DataLoading loading={isLoading}>
          {!isEmpty && validatorStakeInfo && validatorStakeInfo.length !== 0 ? (
            <div className="w-full">
              <div className="flex gap-6 md:flex-col">
                <div className="flex flex-col w-[65%] border-1 border-light-555 rounded-[14px] text-left dark:border-dark-666 md:w-full">
                  <div className="flex gap-2 text-left p-4">
                    <IconContainer style={{ marginTop: '4px' }}>
                      <Icon
                        style={{ height: '36px' }}
                        src={`data:image/${stakerInfo.iconType};base64,${stakerInfo.iconData}`}
                      />
                    </IconContainer>
                    <PosName className="h-[36px] flex flex-col text-light-0 dark:text-white">
                      <span>
                        {stakerInfo.name.startsWith('0x')
                          ? cutString(stakerInfo.name, 8)
                          : stakerInfo.name}
                      </span>
                      <span className="dark:text-dark-0">
                        {isMobile
                          ? cutString(stakerInfo.address, 8)
                          : stakerInfo.address}
                      </span>
                    </PosName>
                    <div className="rounded-lg bg-light-102 text-[12px] px-2 ml-4 flex items-center justify-center text-light-103 dark:bg-dark-103">
                      <img
                        src={workingStats}
                        alt="working"
                        className="w-[14px] h-[14px]"
                      />
                      {stakerInfo.currentStatus}
                    </div>
                  </div>
                  <div className="w-full text-center p-6">
                    <Progress
                      width={160}
                      type="circle"
                      trailColor={isDark ? '#122A3B' : '#EBEFF2'}
                      strokeWidth={5}
                      strokeColor={isDark ? '#0f68aa' : '#0f68aa'}
                      percent={100 - stakerInfo.capacityUsedPercentage}
                      format={() => <CardContent staker={stakerInfo} />}
                    />
                  </div>
                  <div className="flex border-t-1 border-light-555 p-4 dark:border-dark-666 md:flex-col">
                    <div className="flex-1 flex flex-col gap-2 border-r-1 border-light-555 dark:border-dark-666 md:border-r-0 md:border-b-1">
                      <DetailTitle isDark={isDark}>My Stake</DetailTitle>
                      <div style={{ fontSize: '12px' }}>
                        <DetailData isDark={isDark}>{myStakeAmount}</DetailData>
                        <span className="dark:text-dark-104 pl-2">WAN</span>
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col gap-2 border-r-1 border-light-555 pl-[10px] dark:border-dark-666 md:pl-0 md:border-r-0 md:border-b-1 md:pt-[10px]">
                      <DetailTitle isDark={isDark}>My Rewards</DetailTitle>
                      <div style={{ fontSize: '12px' }}>
                        <DetailData isDark={isDark}>{myRewards}</DetailData>
                        <span className="dark:text-dark-104 pl-2">WAN</span>
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col gap-2 pl-[10px] md:pl-0 md:pt-[10px]">
                      <DetailTitle isDark={isDark}>
                        Pending Withdrawal
                      </DetailTitle>
                      <div style={{ fontSize: '12px' }}>
                        <DetailData isDark={isDark}>
                          {pendingWithdrawal}
                        </DetailData>{' '}
                        <span className="dark:text-dark-104">WAN</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 border-1 border-light-555 rounded-[14px] text-left dark:border-dark-666">
                  <div className="p-4 w-full h-[40%] border-b-1 border-light-555 dark:border-dark-666">
                    <InfoItem isDark={isDark} className="text-left">
                      Activity
                    </InfoItem>
                    <div className="flex flex-col my-4 items-end">
                      <div className="p-4 bg-light-555 rounded-lg flex items-center justify-center text-light-0 text-[24px] font-bold dark:bg-dark-777 dark:text-white">
                        {stakerInfo.activity}
                      </div>
                      <Progress
                        trailColor={isDark ? '#122A3B' : '#F2F2F2'}
                        strokeWidth={5}
                        strokeColor={isDark ? '#0f68aa' : '#0f68aa'}
                        percent={stakerInfo.activity.slice(0, -1)}
                        showInfo={false}
                      />
                    </div>
                  </div>
                  <div className="w-full h-[60%] grid grid-cols-2 grid-rows-[1fr,1fr,1fr]">
                    <div className="px-4 border-r-1 border-b-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Delegation Stake</InfoItem>
                      <div>
                        <InfoData isDark={isDark}>{delegationStake}</InfoData>
                        <span className="pl-2 text-light-444">WAN</span>
                      </div>
                    </div>
                    <div className="px-4 border-b-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Accept Delegation</InfoItem>
                      <InfoData isDark={isDark}>
                        {stakerInfo.acceptDelegation}
                      </InfoData>
                    </div>
                    <div className="px-4 border-r-1 border-b-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Delegation Fee (%)</InfoItem>
                      <InfoData isDark={isDark}>
                        {stakerInfo.feeRateFmt}
                      </InfoData>
                    </div>
                    <div className="px-4 border-b-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Max Fee(%)</InfoItem>
                      <InfoData isDark={isDark}>
                        {stakerInfo.maxFeeFmt}
                      </InfoData>
                    </div>
                    <div className="px-4 border-r-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Lock Period</InfoItem>
                      <div>
                        <InfoData isDark={isDark}>
                          {stakerInfo.lockEpochs}
                        </InfoData>
                        <span className="pl-2 text-light-444">DAYS</span>
                      </div>
                    </div>
                    <div className="px-4">
                      <InfoItem isDark={isDark}>Ends In</InfoItem>
                      <InfoData isDark={isDark}>
                        {stakerInfo.nextStatus}
                      </InfoData>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={clsx(
                  'flex justify-between mt-[20px] gap-4 md:flex-col',
                  {
                    'btn-light': !isDark,
                    'btn-dark': isDark,
                  },
                )}
              >
                <Button
                  disabled={validatorStakeInfo[0].nextLockEpochs === 0}
                  className="w-[32%] rounded-4xl h-[40px] text-[18px] md:w-full"
                  onClick={() => setShowTopUpModal(true)}
                >
                  Top-up
                </Button>
                <Button
                  className="w-[32%] rounded-4xl h-[40px] text-[18px] md:w-full"
                  disabled={validatorStakeInfo[0].nextLockEpochs === 0}
                  onClick={() => setShowExitModal(true)}
                >
                  Exit
                </Button>
                <Button
                  className="w-[32%] rounded-4xl h-[40px] text-[18px] md:w-full"
                  disabled={validatorStakeInfo[0].nextLockEpochs === 0}
                  onClick={() => setShowModifyModal(true)}
                >
                  Modify
                </Button>
              </div>
            </div>
          ) : (
            <EmptyData text="Sorry, you don’t have a bridge validator node yet." />
          )}
        </DataLoading>
      </div>
      {showTopUpModal && (
        <TopupModal
          type="Top-up"
          visible={showTopUpModal}
          onCancel={() => setShowTopUpModal(false)}
          stakerInfo={stakerInfo}
        />
      )}
      {showModifyModal &&
        validatorStakeInfo &&
        validatorStakeInfo.length !== 0 && (
          <ModifyModal
            nextLockEpochs={validatorStakeInfo[0].nextLockEpochs}
            visible={showModifyModal}
            onCancel={() => setShowModifyModal(false)}
            stakerInfo={stakerInfo}
          />
        )}
      {showExitModal && (
        <ExitModal
          visible={showExitModal}
          onCancel={() => setShowExitModal(false)}
          stakerInfo={stakerInfo}
          validatorStakeInfo={validatorStakeInfo ? validatorStakeInfo[0] : null}
        />
      )}
    </StakePosContainer>
  )
}
